import React from "react";
import { Text } from "@jsq/ds/core";
import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { parseDateOnlyString } from "../../../../utils/datetime";

interface DateFieldRendererProps {
  value: ExtensionFieldValueObjectType;
  includeTime?: boolean;
}

export const DateFieldRenderer: React.FC<DateFieldRendererProps> = ({
  value,
  includeTime = false,
}) => {
  const dateValue = value.dateValue;

  if (!dateValue) {
    return <Text variant="secondary-md">—</Text>;
  }

  try {
    const dateString = String(dateValue);

    // Use timezone-safe parser for date-only strings to avoid conversion issues
    const parsedDate = parseDateOnlyString(dateString);
    let date: Date;

    if (parsedDate) {
      date = parsedDate;
    } else {
      // Fallback for other date formats (shouldn't happen in normal usage)
      date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return (
          <Text variant="secondary-md" color="danger">
            Invalid date
          </Text>
        );
      }
    }

    // Format based on whether time should be included
    // Use local date methods to avoid timezone conversion issues
    // This ensures consistent display with the date editor
    const formattedDate = includeTime
      ? date.toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : (() => {
          // For date-only fields, use local date methods to avoid timezone shifts
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${month}/${day}/${year}`;
        })();

    return (
      <Text variant="regular-md" title={formattedDate}>
        {formattedDate}
      </Text>
    );
  } catch {
    return (
      <Text variant="secondary-md" color="danger">
        Invalid date
      </Text>
    );
  }
};
